/**
 * Country packs — country-agnostic configuration of Space Academy.
 *
 * Each academy (organization) picks a country; the pack supplies the local
 * labels and formats (identity document, RIF/tax id, phone prefix,
 * date format, currencies). Venezuela-first: `ve` is the default, but the
 * model is extensible — adding a country = 1 entry in `COUNTRY_PACKS`.
 *
 * Each pack exposes a `FeatureManifest` (shape of the self-describing catalog
 * from Design A/C): a platform `CatalogSource` will consume it later,
 * which is why the manifest is kept EXPORTABLE and with non-empty metadata.
 */

/**
 * STRUCTURED consequences of disabling a feature (FLAGS). Declared alongside
 * the manifest and shown in the confirmation dialog of each toggle
 * (platform/org/course). Data is ALWAYS preserved: turning it off blocks
 * new writes and hides UI, never deletes anything.
 */
export interface FeatureImplications {
  /** Navigation entries / screens that disappear. */
  navHidden?: string[];
  /** WRITE flows blocked (reads remain open). */
  flowsBlocked?: string[];
  /** Data policy. Only value: `preserved`. */
  dataPolicy?: "preserved";
}

/**
 * Self-describing metadata of a feature/registry entry. Shape shared
 * with the feature catalog (consumed by a `CatalogSource`).
 *
 * FLAGS — dependency graph:
 * - `requires` is a list of CLAUSES: `"key"` (hard), `"a|b|c"` (at least
 *   one alternative enabled), `?` suffix = soft dependency (composes into
 *   write gates but doesn't block turning off the requirement).
 * - `conflicts` lists keys incompatible with this one.
 * Exact mirror of the Rust `FeatureDescriptor` (`platform/catalog.rs`).
 */
export interface FeatureManifest {
  /** Stable, unique key (namespaced). */
  key: string;
  /** Human-readable name. */
  name: string;
  /** One-line description. */
  description: string;
  /** Scope where the feature applies. */
  scope: "platform" | "org" | "course";
  /** Maturity. */
  status: "stable" | "beta" | "experimental";
  /** Dependency clauses (see interface doc). */
  requires?: string[];
  /** Incompatible features. */
  conflicts?: string[];
  /** Does it have server-side enforcement (`feature_gate`) on any write-path? */
  gateable?: boolean;
  /**
   * FACTORY state when nobody has touched it (no row in `feature_flags`
   * nor org/course override). Absent ⇒ `true`: features are born enabled
   * because they represent capabilities already delivered.
   *
   * `false` describes a capability **built and tested but unused**: the
   * code stays alive and its tests keep running, but the platform doesn't
   * offer it until someone turns it on deliberately (§L1 — in-person).
   */
  defaultEnabled?: boolean;
  /** Since when it exists (informative, e.g. "2026-06"). */
  since?: string;
  /** Consequences of disabling it (confirmation dialog). */
  implications?: FeatureImplications;
  /** In-app help center slug (`/help/<slug>`) with the related guide. */
  docsSlug?: string;
}

/**
 * A country pack describes the local conventions of an academy.
 */
export interface CountryPack {
  /** ISO 3166-1 alpha-2 lowercase (e.g. "ve"). */
  code: string;
  /** Country name. */
  name: string;
  /** Label of the personal identity document (VE: `Cédula`). */
  idLabel: string;
  /** Label of the tax identifier (VE: "RIF"). */
  taxIdLabel: string;
  /** E.164 phone prefix (VE: "+58"). */
  phonePrefix: string;
  /** Preferred date format (VE: "DD/MM/YYYY"). */
  dateFormat: string;
  /** Academy currencies: [primary, ...secondary]. */
  currencies: readonly string[];
  /** Default timezone. */
  defaultTimezone: string;
  /** Default locale (short BCP-47). */
  defaultLocale: string;
  /** Metadata for the self-describing catalog. */
  manifest: FeatureManifest;
}

/** Venezuela pack (default). */
export const VE_PACK: CountryPack = {
  code: "ve",
  name: "Venezuela",
  idLabel: "Cédula",
  taxIdLabel: "RIF",
  phonePrefix: "+58",
  dateFormat: "DD/MM/YYYY",
  currencies: ["USD", "VES"],
  defaultTimezone: "America/Caracas",
  defaultLocale: "es",
  manifest: {
    key: "academy.country.ve",
    name: "Venezuela",
    description:
      "Pack país de Venezuela: cédula/RIF, prefijo +58, fecha DD/MM/YYYY y doble moneda USD/VES (tasa BCV).",
    scope: "org",
    status: "stable",
  },
};

/** Registry of country packs indexed by ISO alpha-2 code (lowercase). */
export const COUNTRY_PACKS: Readonly<Record<string, CountryPack>> = {
  ve: VE_PACK,
};

/** Default pack code (Venezuela-first). */
export const DEFAULT_COUNTRY_CODE = "ve";

/**
 * Returns the pack for country `code` (normalizes uppercase). If it doesn't
 * exist, falls back to the default pack (never throws — the rest of the app always has labels).
 */
export function getCountryPack(code: string | null | undefined): CountryPack {
  if (!code) {
    return VE_PACK;
  }
  return COUNTRY_PACKS[code.toLowerCase()] ?? VE_PACK;
}

/** Lists all available packs. */
export function listCountryPacks(): CountryPack[] {
  return Object.values(COUNTRY_PACKS);
}

/** Manifests of all packs (for the feature catalog). */
export function countryPackManifests(): FeatureManifest[] {
  return listCountryPacks().map((p) => p.manifest);
}

// ─── Dual currency (USD/VES) ───────────────────────────────────────────────────
//
// **Nothing gets converted here anymore.** The USD→VES conversion lives on the
// SERVER (`billing::service::to_ves_minor`), which is the one that has the
// rate, the rounding policy, and the accounting responsibility. This module
// used to have a `convertWithRate` that multiplied in MAJOR units and let
// `Intl` round, while the server rounded to minor unit: the same amount
// could print with two different figures depending on which screen showed it.
//
// The client CONSUMES the `*VesMinor` fields that already arrive converted
// (see `apps/web/src/features/finance/components/money.tsx`). If a new screen
// needs bolivars, the field gets added on the server — a client-side
// conversion is not reintroduced here.
